// src/pages/Wallet.jsx
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils/helpers";
import { useCashfreeSdk } from '../hooks/useCashfreeSdk';
import SOURCE_MAP from "../config/sourceMap";
import {
  ActivityTypeDataEnum,
  TransactionStatusEnum,
  TransactionTypeEnum,
} from "../utils/enums";

// Icons (using simple SVG paths)
const WalletIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

const TrendingUpIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const TrendingDownIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
  </svg>
);

const FilterIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
  </svg>
);

const DownloadIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const SearchIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const RefreshIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const Wallet = () => {
  const navigate = useNavigate();
  const cashfree = useCashfreeSdk();

  // branding source (keeps in sync with other tabs)
  const [source, setSource] = useState(() => {
    try {
      return localStorage.getItem("source") || "wayuvega";
    } catch {
      return "wayuvega";
    }
  });

  useEffect(() => {
    const handler = (e) => {
      if (!e) return;
      if (e.key === "source") {
        setSource(e.newValue || "wayuvega");
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const getLoginRoute = useCallback(() => {
    try {
      const s = localStorage.getItem("source") || source || "wayuvega";
      if (SOURCE_MAP[s] && SOURCE_MAP[s].loginRoute) return SOURCE_MAP[s].loginRoute;
      return "/login";
    } catch {
      return "/login";
    }
  }, [source]);

  // UI state
  const [activeTab, setActiveTab] = useState("Wallet Balance");
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [rechargeTransactions, setRechargeTransactions] = useState([]);
  const [todayTransactions, setTodayTransactions] = useState([]);
  const [isLoadingWallet, setIsLoadingWallet] = useState(false);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [isLoadingRecharge, setIsLoadingRecharge] = useState(false);
  const [isLoadingTodayStats, setIsLoadingTodayStats] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showRechargeFilters, setShowRechargeFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [rechargeSearchQuery, setRechargeSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    transaction_type: "",
    transaction_status: "",
    transaction_date_from: "",
    transaction_date_to: "",
    amount_from: "",
    amount_to: "",
    parent_client_reference_number: "",
  });
  const [rechargeFilters, setRechargeFilters] = useState({
    transaction_status: "",
    transaction_date_from: "",
    transaction_date_to: "",
    amount_from: "",
    amount_to: "",
    parent_client_reference_number: "",
  });

  // Pagination & Sorting for Order Transactions
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState("transaction_date");
  const [sortDirection, setSortDirection] = useState("desc");

  // Pagination & Sorting for Recharge Transactions
  const [rechargeCurrentPage, setRechargeCurrentPage] = useState(1);
  const [rechargeLimit, setRechargeLimit] = useState(10);
  const [rechargeSortBy, setRechargeSortBy] = useState("transaction_date");
  const [rechargeSortDirection, setRechargeSortDirection] = useState("desc");

  // track what server returned (pre-display) and totalResults for correct paging
  const [fetchedCount, setFetchedCount] = useState(0);
  const [totalResults, setTotalResults] = useState(null);
  const [rechargeFetchedCount, setRechargeFetchedCount] = useState(0);
  const [rechargeTotalResults, setRechargeTotalResults] = useState(null);

  // Payment / Recharge
  const [rechargeAmount, setRechargeAmount] = useState("");
  const [isProcessingGateway, setIsProcessingGateway] = useState(false);

  // Helper: ensure login route if no auth
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate(getLoginRoute(), { replace: true });
    }
  }, [getLoginRoute, navigate]);

  // Fetch wallet balance
  const fetchWalletData = useCallback(() => {
    const token = localStorage.getItem("authToken");
    const merchantId = localStorage.getItem("merchantId");
    if (!token || !merchantId) {
      navigate(getLoginRoute(), { replace: true });
      return;
    }

    setIsLoadingWallet(true);
    axios
      .get(`${process.env.REACT_APP_OMS_BASE_URL}/v1/wallet/${merchantId}/balance`, {
        headers: { Authorization: token },
      })
      .then((res) => setWalletData(res.data))
      .catch((err) => {
        console.error("Error fetching wallet balance:", err);
      })
      .finally(() => setIsLoadingWallet(false));
  }, [getLoginRoute, navigate]);

  // Fetch today's transactions for stats display on Balance tab
  const fetchTodayTransactions = useCallback(() => {
    const authToken = localStorage.getItem("authToken");
    const merchantId = localStorage.getItem("merchantId");
    if (!authToken || !merchantId) {
      navigate(getLoginRoute(), { replace: true });
      return;
    }

    // Get today's date range (start and end of day)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStart = today.toISOString();
    
    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 999);
    const todayEndISO = todayEnd.toISOString();

    const params = new URLSearchParams();
    params.append("merchant_id", merchantId);
    params.append("page_number", 1);
    params.append("page_size", 1000); // Get more records for accurate stats
    params.append("sort_by", "transaction_date");
    params.append("sort_direction", "desc");
    params.append("transaction_date_from", todayStart);
    params.append("transaction_date_to", todayEndISO);
    
    // Get non-VOID transactions
    ["PENDING", "CONFIRMED", "FAILED"].forEach((s) => params.append("transaction_status", s));

    const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}/v1/wallet/transactions?${params.toString()}`;

    setIsLoadingTodayStats(true);
    axios
      .get(apiUrl, {
        headers: { Authorization: authToken, Accept: "application/json" },
      })
      .then((response) => {
        const data = response.data.transactions || [];
        
        const formattedData = data.map((transaction) => ({
          walletTransactionId: transaction.wallet_transaction_id,
          walletId: transaction.wallet_id,
          client_id: transaction.client_id,
          orderReferenceNumber: transaction.parent_client_reference_number || "N/A",
          transactionAmount: transaction.transaction_amount,
          description: transaction.transaction_description || "N/A",
          transactionDate: formatDate(transaction.transaction_date),
          rawDate: transaction.transaction_date,
          activityType: transaction.activity_type,
          activityTypeName:
            Object.values(ActivityTypeDataEnum).find((type) => type.id === transaction.activity_type)?.name ||
            "Unknown",
          transactionStatusName:
            Object.values(TransactionStatusEnum).find((status) => status.id === transaction.transaction_status_id)
              ?.name || "Unknown",
          transactionTypeName:
            Object.values(TransactionTypeEnum).find((type) => type.id === transaction.transaction_type_id)?.name ||
            "Unknown",
          transactionStatusId: transaction.transaction_status_id,
          transactionTypeId: transaction.transaction_type_id,
        }));

        setTodayTransactions(formattedData);
      })
      .catch((error) => {
        console.error("Error fetching today's transactions:", error);
      })
      .finally(() => setIsLoadingTodayStats(false));
  }, [getLoginRoute, navigate]);

  const buildQueryParams = useCallback((options = {}) => {
    const {
      page = currentPage,
      pageSize = limit,
      sort = sortBy,
      sortDir = sortDirection,
      filterSet = filters,
      activityType = null,
    } = options;

    const merchantId = localStorage.getItem("merchantId");
    const params = new URLSearchParams();
    if (merchantId) params.append("merchant_id", merchantId);
    params.append("page_number", page);
    params.append("page_size", pageSize);
    params.append("sort_by", sort);
    params.append("sort_direction", sortDir);

    if (activityType !== null && activityType === -2) {
      params.append("activity_type", "-2");
    }

    if (filterSet.transaction_type) params.append("transaction_type", filterSet.transaction_type);

    if (filterSet.transaction_status) {
      params.append("transaction_status", filterSet.transaction_status.toString().toUpperCase());
    } else {
      ["PENDING", "CONFIRMED", "FAILED"].forEach((s) => params.append("transaction_status", s));
    }

    if (filterSet.transaction_date_from)
      params.append("transaction_date_from", new Date(filterSet.transaction_date_from).toISOString());
    if (filterSet.transaction_date_to)
      params.append("transaction_date_to", new Date(filterSet.transaction_date_to).toISOString());
    if (filterSet.amount_from) params.append("amount_from", filterSet.amount_from);
    if (filterSet.amount_to) params.append("amount_to", filterSet.amount_to);
    if (filterSet.parent_client_reference_number)
      params.append("parent_client_reference_number", filterSet.parent_client_reference_number.trim());

    return params;
  }, [currentPage, filters, limit, sortBy, sortDirection]);

  const fetchTransactionData = useCallback(() => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      navigate(getLoginRoute(), { replace: true });
      return;
    }

    const params = buildQueryParams();
    const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}/v1/wallet/transactions?${params.toString()}`;

    setIsLoadingTransactions(true);
    axios
      .get(apiUrl, {
        headers: { Authorization: authToken, Accept: "application/json" },
      })
      .then((response) => {
        let data = response.data.transactions || [];
        data = data.filter((transaction) => transaction.activity_type !== -2);
        
        setFetchedCount(data.length);

        if (typeof response.data.totalResults !== "undefined" && response.data.totalResults !== null) {
          setTotalResults(Number(response.data.totalResults));
        } else {
          setTotalResults(null);
        }

        const formattedData = data.map((transaction) => ({
          walletTransactionId: transaction.wallet_transaction_id,
          walletId: transaction.wallet_id,
          client_id: transaction.client_id,
          orderReferenceNumber: transaction.parent_client_reference_number || "N/A",
          transactionAmount: transaction.transaction_amount,
          description: transaction.transaction_description || "N/A",
          transactionDate: formatDate(transaction.transaction_date),
          rawDate: transaction.transaction_date,
          activityType: transaction.activity_type,
          activityTypeName:
            Object.values(ActivityTypeDataEnum).find((type) => type.id === transaction.activity_type)?.name ||
            "Unknown",
          transactionStatusName:
            Object.values(TransactionStatusEnum).find((status) => status.id === transaction.transaction_status_id)
              ?.name || "Unknown",
          transactionTypeName:
            Object.values(TransactionTypeEnum).find((type) => type.id === transaction.transaction_type_id)?.name ||
            "Unknown",
          transactionStatusId: transaction.transaction_status_id,
          transactionTypeId: transaction.transaction_type_id,
        }));

        setTransactions(formattedData);
      })
      .catch((error) => {
        console.error("Error fetching transactions:", error);
      })
      .finally(() => setIsLoadingTransactions(false));
  }, [buildQueryParams, getLoginRoute, navigate]);

  const fetchRechargeData = useCallback(() => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      navigate(getLoginRoute(), { replace: true });
      return;
    }

    const params = buildQueryParams({
      page: rechargeCurrentPage,
      pageSize: rechargeLimit,
      sort: rechargeSortBy,
      sortDir: rechargeSortDirection,
      filterSet: rechargeFilters,
      activityType: -2,
    });
    const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}/v1/wallet/transactions?${params.toString()}`;

    setIsLoadingRecharge(true);
    axios
      .get(apiUrl, {
        headers: { Authorization: authToken, Accept: "application/json" },
      })
      .then((response) => {
        let data = response.data.transactions || [];
        const originalCount = data.length;
        
        data = data.filter((transaction) => transaction.activity_type === -2);
        
        setRechargeFetchedCount(data.length);

        if (typeof response.data.totalResults !== "undefined" && response.data.totalResults !== null) {
          if (originalCount > 0 && data.length === 0) {
            setRechargeTotalResults(null);
          } else {
            setRechargeTotalResults(Number(response.data.totalResults));
          }
        } else {
          setRechargeTotalResults(null);
        }

        const formattedData = data.map((transaction) => ({
          walletTransactionId: transaction.wallet_transaction_id,
          walletId: transaction.wallet_id,
          client_id: transaction.client_id,
          orderReferenceNumber: transaction.parent_client_reference_number || "N/A",
          transactionAmount: transaction.transaction_amount,
          description: transaction.transaction_description || "N/A",
          transactionDate: formatDate(transaction.transaction_date),
          rawDate: transaction.transaction_date,
          activityType: transaction.activity_type,
          activityTypeName:
            Object.values(ActivityTypeDataEnum).find((type) => type.id === transaction.activity_type)?.name ||
            "Recharge Wallet",
          transactionStatusName:
            Object.values(TransactionStatusEnum).find((status) => status.id === transaction.transaction_status_id)
              ?.name || "Unknown",
          transactionTypeName:
            Object.values(TransactionTypeEnum).find((type) => type.id === transaction.transaction_type_id)?.name ||
            "Unknown",
          transactionStatusId: transaction.transaction_status_id,
          transactionTypeId: transaction.transaction_type_id,
        }));

        setRechargeTransactions(formattedData);
      })
      .catch((error) => {
        console.error("Error fetching recharge transactions:", error);
      })
      .finally(() => setIsLoadingRecharge(false));
  }, [buildQueryParams, getLoginRoute, navigate, rechargeCurrentPage, rechargeLimit, rechargeSortBy, rechargeSortDirection, rechargeFilters]);

  useEffect(() => {
    if (activeTab === "Wallet Balance") {
      fetchWalletData();
      fetchTodayTransactions();
    } else if (activeTab === "Order Transactions History") {
      setCurrentPage(1);
      fetchTodayTransactions();
    } else if (activeTab === "Recharge History") {
      setRechargeCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "Order Transactions History") {
      fetchTransactionData();
    }
  }, [activeTab, currentPage, limit, sortBy, sortDirection, filters, fetchTransactionData]);

  useEffect(() => {
    if (activeTab === "Recharge History") {
      fetchRechargeData();
    }
  }, [activeTab, rechargeCurrentPage, rechargeLimit, rechargeSortBy, rechargeSortDirection, rechargeFilters, fetchRechargeData]);

  // Filter handlers
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchTransactionData();
  };

  const handleResetFilters = () => {
    setFilters({
      transaction_type: "",
      transaction_status: "",
      transaction_date_from: "",
      transaction_date_to: "",
      amount_from: "",
      amount_to: "",
      parent_client_reference_number: "",
    });
    setCurrentPage(1);
    fetchTransactionData();
  };

  // Recharge filter handlers
  const handleRechargeFilterChange = (e) => {
    const { name, value } = e.target;
    setRechargeFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyRechargeFilters = () => {
    setRechargeCurrentPage(1);
    fetchRechargeData();
  };

  const handleResetRechargeFilters = () => {
    setRechargeFilters({
      transaction_status: "",
      transaction_date_from: "",
      transaction_date_to: "",
      amount_from: "",
      amount_to: "",
      parent_client_reference_number: "",
    });
    setRechargeCurrentPage(1);
    fetchRechargeData();
  };

  // Recharge sorting
  const handleRechargeSort = (column) => {
    if (rechargeSortBy === column) {
      setRechargeSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setRechargeSortBy(column);
      setRechargeSortDirection("asc");
    }
    setRechargeCurrentPage(1);
  };

  // Recharge pagination
  const rechargePrevPage = () => {
    if (rechargeCurrentPage > 1) setRechargeCurrentPage((p) => p - 1);
  };
  const rechargeNextPage = () => {
    if (rechargeTotalResults !== null) {
      if (rechargeCurrentPage * rechargeLimit < rechargeTotalResults) setRechargeCurrentPage((p) => p + 1);
    } else {
      if (rechargeFetchedCount === rechargeLimit) setRechargeCurrentPage((p) => p + 1);
    }
  };
  const handleRechargeLimitChange = (e) => {
    setRechargeLimit(Number(e.target.value));
    setRechargeCurrentPage(1);
  };

  // Sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  // Pagination
  const prevPage = () => {
    if (currentPage > 1) setCurrentPage((p) => p - 1);
  };
  const nextPage = () => {
    // Prefer exact totalResults if provided by API, otherwise fallback to fetchedCount === limit heuristic
    if (totalResults !== null) {
      if (currentPage * limit < totalResults) setCurrentPage((p) => p + 1);
    } else {
      if (fetchedCount === limit) setCurrentPage((p) => p + 1);
    }
  };
  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setCurrentPage(1);
  };

  // Recharge / payment
  const handlePay = () => {
    if (!rechargeAmount || Number(rechargeAmount) <= 0) {
      alert("Please enter a valid amount to recharge.");
      return;
    }
    handleGatewayRecharge();
  };

  const handleGatewayRecharge = async () => {
    setIsProcessingGateway(true);
    try {
      const authToken = localStorage.getItem("authToken");
      const merchantId = localStorage.getItem("merchantId");
      const parentClientId = localStorage.getItem("parentClientId");
      const rawProfileData = localStorage.getItem("profileData");
      const profile = rawProfileData ? JSON.parse(rawProfileData) : {};
      const customer_email = profile.basic_details?.email;
      const customer_phone = profile.basic_details?.phone;

      const RECHARGE_API = `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_RECHARGE_THROUGH_PAYMENT_GATEWAY}`;
      const body = {
        amount: {
          currency_code: "INR",
          transaction_amount: Number(rechargeAmount),
        },
        transaction_description: "Wallet recharge",
        client_id: parentClientId,
        parent_client_id: parentClientId,
        parent_client_reference_number: `RECHG-${Date.now()}`,
        client_reference_number: `RECHG-${Date.now()}`,
        merchant_id: merchantId,
        payment_service_provider: "Cashfree",
        client_email: customer_email,
        client_phone: customer_phone,
        recharge_type: "PAYMENT_GATEWAY",
        remarks: "Recharge",
      };

      const resp = await axios.post(RECHARGE_API, body, {
        headers: { Authorization: authToken },
      });
      const paymentSessionId = resp.data.paymentSessionId;

      if (!cashfree) {
        alert("Cashfree SDK is still initializing. Please wait a few seconds and try again.");
        return;
      }

      cashfree.checkout({
        paymentSessionId,
        redirectTarget: "_modal",
        onSuccess: (payload) => {
          console.log("✅ CF Success", payload);
          fetchWalletData();
          fetchTodayTransactions();
          // Refresh recharge history if on that tab
          if (activeTab === "Recharge History") {
            fetchRechargeData();
          }
        },
        onFailure: (err) => {
          console.error("❌ CF Failure", err);
          alert("Payment failed or was cancelled");
        },
      });
    } catch (err) {
      console.error("Recharge API error:", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsProcessingGateway(false);
    }
  };

  // Helper: Get status badge color
  const getStatusBadgeClass = (statusName) => {
    const status = statusName?.toLowerCase();
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'void':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  // Helper: Get transaction type badge color
  const getTypeBadgeClass = (typeName) => {
    const type = typeName?.toLowerCase();
    switch (type) {
      case 'deposit':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'withdraw':
        return 'bg-rose-100 text-rose-800 border-rose-200';
      case 'refund':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  // Calculate transaction stats
  const getTransactionStats = (useToday = false) => {
    // Use today's transactions for Balance tab, all transactions for History tab
    const dataSource = useToday ? todayTransactions : transactions;
    const totalTransactions = useToday 
      ? todayTransactions.length 
      : (totalResults || transactions.length);
    
    const totalDebit = dataSource
      .filter(tx => tx.transactionTypeId === 2) // Assuming 2 is withdraw/debit
      .reduce((sum, tx) => sum + Number(tx.transactionAmount), 0);
    const totalCredit = dataSource
      .filter(tx => tx.transactionTypeId === 1) // Assuming 1 is deposit/credit
      .reduce((sum, tx) => sum + Number(tx.transactionAmount), 0);
    
    return { totalTransactions, totalDebit, totalCredit };
  };

  // Recharge filter UI
  const renderRechargeFilters = () => (
    <div className={`transition-all duration-300 overflow-hidden ${showRechargeFilters ? 'max-h-[1000px] opacity-100 mb-6' : 'max-h-0 opacity-0'}`}>
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg border border-gray-200 p-4 lg:p-6">
        <div className="flex items-center justify-between mb-4 lg:mb-5">
          <h3 className="text-base lg:text-lg font-bold text-gray-900 flex items-center gap-2">
            <FilterIcon />
            <span className="hidden sm:inline">Advanced</span> Filters
          </h3>
          <button
            onClick={() => setShowRechargeFilters(false)}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-5">
          <div className="space-y-2">
            <label className="block text-xs lg:text-sm font-semibold text-gray-700">
              Reference Number
            </label>
            <div className="relative">
              <input
                type="text"
                name="parent_client_reference_number"
                value={rechargeFilters.parent_client_reference_number}
                onChange={handleRechargeFilterChange}
                placeholder="e.g. RECHG-1234567890"
                className="w-full border border-gray-300 rounded-lg px-3 lg:px-4 py-2 lg:py-2.5 text-sm lg:text-base text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-xs lg:text-sm font-semibold text-gray-700">
              Transaction Status
            </label>
            <select
              name="transaction_status"
              value={rechargeFilters.transaction_status}
              onChange={handleRechargeFilterChange}
              className="w-full border border-gray-300 rounded-lg px-3 lg:px-4 py-2 lg:py-2.5 text-sm lg:text-base text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="void">Void</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-xs lg:text-sm font-semibold text-gray-700">
              Date From
            </label>
            <input
              type="date"
              name="transaction_date_from"
              value={rechargeFilters.transaction_date_from}
              onChange={handleRechargeFilterChange}
              className="w-full border border-gray-300 rounded-lg px-3 lg:px-4 py-2 lg:py-2.5 text-sm lg:text-base text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-xs lg:text-sm font-semibold text-gray-700">
              Date To
            </label>
            <input
              type="date"
              name="transaction_date_to"
              value={rechargeFilters.transaction_date_to}
              onChange={handleRechargeFilterChange}
              className="w-full border border-gray-300 rounded-lg px-3 lg:px-4 py-2 lg:py-2.5 text-sm lg:text-base text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-xs lg:text-sm font-semibold text-gray-700">
              Amount Range
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                name="amount_from"
                value={rechargeFilters.amount_from}
                onChange={handleRechargeFilterChange}
                placeholder="Min"
                className="w-1/2 border border-gray-300 rounded-lg px-2 lg:px-4 py-2 lg:py-2.5 text-sm lg:text-base text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
              <input
                type="number"
                name="amount_to"
                value={rechargeFilters.amount_to}
                onChange={handleRechargeFilterChange}
                placeholder="Max"
                className="w-1/2 border border-gray-300 rounded-lg px-2 lg:px-4 py-2 lg:py-2.5 text-sm lg:text-base text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-5 lg:mt-6 pt-4 lg:pt-5 border-t border-gray-200">
          <button
            onClick={handleApplyRechargeFilters}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 lg:px-6 py-2 lg:py-2.5 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg font-semibold text-sm lg:text-base"
          >
            <FilterIcon />
            Apply Filters
          </button>
          <button
            onClick={handleResetRechargeFilters}
            className="flex items-center gap-2 bg-white border-2 border-gray-300 text-gray-700 px-4 lg:px-6 py-2 lg:py-2.5 rounded-lg hover:bg-gray-50 transition-all font-semibold text-sm lg:text-base"
          >
            <RefreshIcon />
            Reset All
          </button>
        </div>
      </div>
    </div>
  );

  // Transaction filter UI
  const renderTransactionFilters = () => (
    <div className={`transition-all duration-300 overflow-hidden ${showFilters ? 'max-h-[1000px] opacity-100 mb-6' : 'max-h-0 opacity-0'}`}>
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg border border-gray-200 p-4 lg:p-6">
        <div className="flex items-center justify-between mb-4 lg:mb-5">
          <h3 className="text-base lg:text-lg font-bold text-gray-900 flex items-center gap-2">
            <FilterIcon />
            <span className="hidden sm:inline">Advanced</span> Filters
          </h3>
          <button
            onClick={() => setShowFilters(false)}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-5">
          <div className="space-y-2">
            <label className="block text-xs lg:text-sm font-semibold text-gray-700">
              Order Reference Number
            </label>
            <div className="relative">
              <input
                type="text"
                name="parent_client_reference_number"
                value={filters.parent_client_reference_number}
                onChange={handleFilterChange}
                placeholder="e.g. 405-3009931-6301112"
                className="w-full border border-gray-300 rounded-lg px-3 lg:px-4 py-2 lg:py-2.5 text-sm lg:text-base text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-xs lg:text-sm font-semibold text-gray-700">
              Transaction Type
            </label>
            <select
              name="transaction_type"
              value={filters.transaction_type}
              onChange={handleFilterChange}
              className="w-full border border-gray-300 rounded-lg px-3 lg:px-4 py-2 lg:py-2.5 text-sm lg:text-base text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
            >
              <option value="">All Types</option>
              <option value="deposit">Deposit</option>
              <option value="withdraw">Withdraw</option>
              <option value="refund">Refund</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-xs lg:text-sm font-semibold text-gray-700">
              Transaction Status
            </label>
            <select
              name="transaction_status"
              value={filters.transaction_status}
              onChange={handleFilterChange}
              className="w-full border border-gray-300 rounded-lg px-3 lg:px-4 py-2 lg:py-2.5 text-sm lg:text-base text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="void">Void</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-xs lg:text-sm font-semibold text-gray-700">
              Date From
            </label>
            <input
              type="date"
              name="transaction_date_from"
              value={filters.transaction_date_from}
              onChange={handleFilterChange}
              className="w-full border border-gray-300 rounded-lg px-3 lg:px-4 py-2 lg:py-2.5 text-sm lg:text-base text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-xs lg:text-sm font-semibold text-gray-700">
              Date To
            </label>
            <input
              type="date"
              name="transaction_date_to"
              value={filters.transaction_date_to}
              onChange={handleFilterChange}
              className="w-full border border-gray-300 rounded-lg px-3 lg:px-4 py-2 lg:py-2.5 text-sm lg:text-base text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-xs lg:text-sm font-semibold text-gray-700">
              Amount Range
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                name="amount_from"
                value={filters.amount_from}
                onChange={handleFilterChange}
                placeholder="Min"
                className="w-1/2 border border-gray-300 rounded-lg px-2 lg:px-4 py-2 lg:py-2.5 text-sm lg:text-base text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
              <input
                type="number"
                name="amount_to"
                value={filters.amount_to}
                onChange={handleFilterChange}
                placeholder="Max"
                className="w-1/2 border border-gray-300 rounded-lg px-2 lg:px-4 py-2 lg:py-2.5 text-sm lg:text-base text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-5 lg:mt-6 pt-4 lg:pt-5 border-t border-gray-200">
          <button
            onClick={handleApplyFilters}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 lg:px-6 py-2 lg:py-2.5 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg font-semibold text-sm lg:text-base"
          >
            <FilterIcon />
            Apply Filters
          </button>
          <button
            onClick={handleResetFilters}
            className="flex items-center gap-2 bg-white border-2 border-gray-300 text-gray-700 px-4 lg:px-6 py-2 lg:py-2.5 rounded-lg hover:bg-gray-50 transition-all font-semibold text-sm lg:text-base"
          >
            <RefreshIcon />
            Reset All
          </button>
        </div>
      </div>
    </div>
  );

  const renderWalletBalance = () => {
    const stats = getTransactionStats(true); // Use today's transactions

    return (
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left Column - Main Wallet Card & Recharge (2/3 width on XL) */}
        <div className="xl:col-span-2 space-y-6">
          {/* Main Wallet Card */}
          <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 rounded-2xl shadow-2xl p-8 text-white relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -mr-32 -mt-32"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full -ml-24 -mb-24"></div>
            </div>

            <div className="relative z-10">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="bg-white bg-opacity-20 p-2 rounded-xl backdrop-blur-sm">
                    <WalletIcon />
                  </div>
                  <div>
                    <p className="text-blue-100 text-sm font-medium">Total Wallet Balance</p>
                    <p className="text-xs text-blue-200 mt-0.5">
                      Account Type: <span className="font-semibold capitalize">{walletData?.type || 'N/A'}</span>
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    fetchWalletData();
                    fetchTodayTransactions();
                  }}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 p-2.5 rounded-lg transition-all backdrop-blur-sm"
                  title="Refresh balance"
                >
                  <RefreshIcon />
                </button>
              </div>

              {isLoadingWallet ? (
                <div className="space-y-3 animate-pulse">
                  <div className="h-12 bg-white bg-opacity-20 rounded-lg w-3/4"></div>
                  <div className="h-6 bg-white bg-opacity-20 rounded-lg w-1/2"></div>
                </div>
              ) : walletData ? (
                <>
                  <div className="mb-6">
                    <h2 className="text-5xl md:text-6xl font-bold mb-2">
                      {new Intl.NumberFormat("en-IN", {
                        style: "currency",
                        currency: "INR",
                        maximumFractionDigits: 2,
                      }).format(walletData.total_available_balance)}
                    </h2>
                    <p className="text-blue-100 text-sm">Available to use</p>
                  </div>

                  {walletData.total_balance !== walletData.total_available_balance && (
                    <div className="bg-white bg-opacity-10 rounded-xl p-4 backdrop-blur-sm">
                      <p className="text-blue-100 text-sm mb-1">Total Balance (incl. pending)</p>
                      <p className="text-2xl font-semibold">
                        {new Intl.NumberFormat("en-IN", {
                          style: "currency",
                          currency: "INR",
                          maximumFractionDigits: 2,
                        }).format(walletData.total_balance)}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-lg">Unable to load wallet data</p>
              )}
            </div>
          </div>

          {/* Recharge Section */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-3 rounded-xl text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              Recharge Wallet
            </h3>

            <div className="space-y-6">
              {/* Quick Amount Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Quick Select Amount
                </label>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                  {[5000, 10000, 20000, 50000, 100000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setRechargeAmount(amount)}
                      className={`px-4 py-3 rounded-xl font-semibold transition-all border-2 ${
                        Number(rechargeAmount) === amount
                          ? 'bg-blue-600 text-white border-blue-600 shadow-lg scale-105'
                          : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100 hover:border-gray-300'
                      }`}
                    >
                      ₹{amount.toLocaleString('en-IN')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Amount Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Or Enter Custom Amount
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-lg">
                    ₹
                  </span>
                  <input
                    type="number"
                    value={rechargeAmount}
                    onChange={(e) => setRechargeAmount(e.target.value)}
                    className="w-full border-2 border-gray-300 rounded-xl pl-10 pr-4 py-3.5 text-gray-800 text-lg font-semibold focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="Enter amount"
                    min="1"
                  />
                </div>
                {rechargeAmount && Number(rechargeAmount) > 0 && (
                  <p className="text-sm text-gray-600 mt-2">
                    You'll be charged: <span className="font-semibold">₹{Number(rechargeAmount).toLocaleString('en-IN')}</span>
                  </p>
                )}
              </div>

              {/* Pay Button */}
              <button
                onClick={handleGatewayRecharge}
                disabled={isProcessingGateway || !rechargeAmount || Number(rechargeAmount) <= 0}
                className={`w-full py-4 text-white rounded-xl font-bold text-lg transition-all shadow-lg ${
                  isProcessingGateway || !rechargeAmount || Number(rechargeAmount) <= 0
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 hover:shadow-xl transform hover:-translate-y-0.5'
                }`}
              >
                {isProcessingGateway ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing Payment...
                  </span>
                ) : (
                  `Proceed to Pay ${rechargeAmount ? `₹${Number(rechargeAmount).toLocaleString('en-IN')}` : ''}`
                )}
              </button>

              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <p className="text-sm text-blue-800">
                  <span className="font-semibold">💡 Tip:</span> We use secure payment gateway (Cashfree) for all transactions. Your payment information is encrypted and secure.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Quick Stats (1/3 width on XL) */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Today's Stats</h3>
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {new Date().toLocaleDateString('en-IN', { 
                    day: 'numeric', 
                    month: 'short', 
                    year: 'numeric' 
                  })}
                </p>
              </div>
              <div className="bg-blue-100 p-2 rounded-lg">
                {isLoadingTodayStats ? (
                  <svg className="animate-spin h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                )}
              </div>
            </div>
            
            {isLoadingTodayStats ? (
              <div className="space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="animate-pulse bg-gray-100 rounded-lg h-24"></div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-4 border border-emerald-200">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUpIcon />
                    <p className="text-sm font-medium text-emerald-800">Total Credit</p>
                  </div>
                  <p className="text-2xl font-bold text-emerald-700">
                    ₹{stats.totalCredit.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-rose-50 to-rose-100 rounded-lg p-4 border border-rose-200">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingDownIcon />
                    <p className="text-sm font-medium text-rose-800">Total Debit</p>
                  </div>
                  <p className="text-2xl font-bold text-rose-700">
                    ₹{stats.totalDebit.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm font-medium text-blue-800">Total Transactions</p>
                  </div>
                  <p className="text-2xl font-bold text-blue-700">
                    {stats.totalTransactions.toLocaleString('en-IN')}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                    </svg>
                    <p className="text-sm font-medium text-purple-800">Net Flow</p>
                  </div>
                  <p className="text-2xl font-bold text-purple-700">
                    ₹{(stats.totalCredit - stats.totalDebit).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Recent Activity Indicator */}
          <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
            <h4 className="font-bold text-lg mb-2">Quick Actions</h4>
            <p className="text-sm text-indigo-100 mb-4">View your complete transaction history</p>
            <button
              onClick={() => setActiveTab("Order Transactions History")}
              className="w-full bg-white text-indigo-600 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-all"
            >
              View All Transactions
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderOrderTransactionsHistory = () => {
    const todayStats = getTransactionStats(true); // Use today's transactions for top stats cards
    const filteredTransactions = searchQuery
      ? transactions.filter(tx =>
          tx.orderReferenceNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
          tx.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          tx.transactionTypeName.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : transactions;

    return (
      <div className="space-y-6">
        {/* Stats Summary Cards - Full Width Grid - Shows TODAY'S data */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Today's Overview</h3>
              <p className="text-sm text-gray-600 flex items-center gap-1.5 mt-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {new Date().toLocaleDateString('en-IN', { 
                  day: 'numeric', 
                  month: 'short', 
                  year: 'numeric',
                  weekday: 'long'
                })}
              </p>
            </div>
            {isLoadingTodayStats && (
              <div className="flex items-center gap-2 text-blue-600 text-sm">
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Updating...
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white hover:shadow-xl transition-all">
              <div className="flex items-center justify-between mb-3">
                <p className="text-blue-100 text-sm font-medium">Total Transactions</p>
                <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
              {isLoadingTodayStats ? (
                <div className="h-10 bg-white bg-opacity-20 rounded animate-pulse"></div>
              ) : (
                <p className="text-3xl lg:text-4xl font-bold">{todayStats.totalTransactions.toLocaleString('en-IN')}</p>
              )}
            </div>

            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl shadow-lg p-6 text-white hover:shadow-xl transition-all">
              <div className="flex items-center justify-between mb-3">
                <p className="text-emerald-100 text-sm font-medium">Total Credit</p>
                <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                  <TrendingUpIcon />
                </div>
              </div>
              {isLoadingTodayStats ? (
                <div className="h-10 bg-white bg-opacity-20 rounded animate-pulse"></div>
              ) : (
                <p className="text-3xl lg:text-4xl font-bold">₹{todayStats.totalCredit.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
              )}
            </div>

            <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-xl shadow-lg p-6 text-white hover:shadow-xl transition-all">
              <div className="flex items-center justify-between mb-3">
                <p className="text-rose-100 text-sm font-medium">Total Debit</p>
                <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                  <TrendingDownIcon />
                </div>
              </div>
              {isLoadingTodayStats ? (
                <div className="h-10 bg-white bg-opacity-20 rounded animate-pulse"></div>
              ) : (
                <p className="text-3xl lg:text-4xl font-bold">₹{todayStats.totalDebit.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
              )}
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white hover:shadow-xl transition-all">
              <div className="flex items-center justify-between mb-3">
                <p className="text-purple-100 text-sm font-medium">Net Flow</p>
                <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                  </svg>
                </div>
              </div>
              {isLoadingTodayStats ? (
                <div className="h-10 bg-white bg-opacity-20 rounded animate-pulse"></div>
              ) : (
                <p className="text-3xl lg:text-4xl font-bold">
                  ₹{(todayStats.totalCredit - todayStats.totalDebit).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Action Bar - Full Width */}
        <div className="bg-white rounded-xl shadow-lg p-5 border border-gray-200">
          <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center justify-between">
            {/* Search Bar */}
            <div className="relative flex-1">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                <SearchIcon />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by order ref, description, transaction type..."
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
              
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold transition-all ${
                  showFilters
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <FilterIcon />
                <span className="hidden sm:inline">Filters</span>
                <ChevronDownIcon className={`transform transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>

              <button
                onClick={() => {
                  fetchTodayTransactions(); // Refresh today's stats
                  fetchTransactionData(); // Refresh transaction table
                }}
                disabled={isLoadingTransactions || isLoadingTodayStats}
                className="flex items-center gap-2 px-5 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold transition-all disabled:opacity-50"
                title="Refresh transactions"
              >
                <RefreshIcon className={(isLoadingTransactions || isLoadingTodayStats) ? 'animate-spin' : ''} />
                <span className="hidden sm:inline">Refresh</span>
              </button>

              <button
                onClick={() => alert('Export functionality - Coming soon!')}
                className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 font-semibold transition-all shadow-md"
              >
                <DownloadIcon />
                <span className="hidden sm:inline">Export</span>
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        {renderTransactionFilters()}

        {/* Transactions Table - Full Width */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            {isLoadingTransactions ? (
              <div className="p-8 space-y-4">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div key={i} className="animate-pulse flex gap-4">
                    <div className="h-12 bg-gray-200 rounded flex-1"></div>
                    <div className="h-12 bg-gray-200 rounded w-32"></div>
                    <div className="h-12 bg-gray-200 rounded w-32"></div>
                    <div className="h-12 bg-gray-200 rounded w-32"></div>
                  </div>
                ))}
              </div>
            ) : filteredTransactions.length > 0 ? (
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                  <tr>
                    <th
                      className="px-3 lg:px-6 py-3 lg:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort("parent_client_reference_number")}
                    >
                      <div className="flex items-center gap-1 lg:gap-2">
                        <span className="hidden sm:inline">Order</span> Ref
                        {sortBy === "parent_client_reference_number" && (
                          <span className="text-blue-600">{sortDirection === "asc" ? "↑" : "↓"}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="hidden md:table-cell px-3 lg:px-6 py-3 lg:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort("transaction_type_id")}
                    >
                      <div className="flex items-center gap-2">
                        Type
                        {sortBy === "transaction_type_id" && (
                          <span className="text-blue-600">{sortDirection === "asc" ? "↑" : "↓"}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-3 lg:px-6 py-3 lg:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort("transaction_amount")}
                    >
                      <div className="flex items-center gap-1 lg:gap-2">
                        Amount
                        {sortBy === "transaction_amount" && (
                          <span className="text-blue-600">{sortDirection === "asc" ? "↑" : "↓"}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="hidden lg:table-cell px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort("activity_type")}
                    >
                      <div className="flex items-center gap-2">
                        Activity
                        {sortBy === "activity_type" && (
                          <span className="text-blue-600">{sortDirection === "asc" ? "↑" : "↓"}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-3 lg:px-6 py-3 lg:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort("transaction_status_id")}
                    >
                      <div className="flex items-center gap-1 lg:gap-2">
                        Status
                        {sortBy === "transaction_status_id" && (
                          <span className="text-blue-600">{sortDirection === "asc" ? "↑" : "↓"}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="hidden xl:table-cell px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort("transaction_date")}
                    >
                      <div className="flex items-center gap-2">
                        Date & Time
                        {sortBy === "transaction_date" && (
                          <span className="text-blue-600">{sortDirection === "asc" ? "↑" : "↓"}</span>
                        )}
                      </div>
                    </th>
                    <th className="hidden xl:table-cell px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredTransactions.map((tx, idx) => (
                    <tr
                      key={tx.walletTransactionId || idx}
                      className="hover:bg-blue-50 transition-colors duration-150"
                    >
                      <td className="px-3 lg:px-6 py-3 lg:py-4">
                        <div className="flex flex-col">
                          <div className="text-xs lg:text-sm font-medium text-gray-900 break-all">
                            {tx.orderReferenceNumber}
                          </div>
                          {/* Show date on mobile/tablet (hidden on XL+) */}
                          <div className="text-xs text-gray-500 mt-1 xl:hidden">
                            {tx.transactionDate}
                          </div>
                          {/* Show activity on mobile (hidden on LG+) */}
                          <div className="text-xs text-gray-600 mt-1 lg:hidden">
                            {tx.activityTypeName}
                          </div>
                        </div>
                      </td>
                      <td className="hidden md:table-cell px-3 lg:px-6 py-3 lg:py-4">
                        <span className={`px-2 lg:px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getTypeBadgeClass(tx.transactionTypeName)}`}>
                          {tx.transactionTypeName}
                        </span>
                      </td>
                      <td className="px-3 lg:px-6 py-3 lg:py-4">
                        <div className="flex flex-col">
                          <div className={`text-xs lg:text-sm font-bold whitespace-nowrap ${tx.transactionTypeId === 2 ? 'text-rose-600' : 'text-emerald-600'}`}>
                            {tx.transactionTypeId === 2 ? '- ' : '+ '}
                            ₹{Number(tx.transactionAmount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                          </div>
                          {/* Show type badge on mobile (hidden on MD+) */}
                          <span className={`md:hidden mt-1 px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full border w-fit ${getTypeBadgeClass(tx.transactionTypeName)}`}>
                            {tx.transactionTypeName}
                          </span>
                        </div>
                      </td>
                      <td className="hidden lg:table-cell px-6 py-4">
                        <div className="text-sm text-gray-900">{tx.activityTypeName}</div>
                      </td>
                      <td className="px-3 lg:px-6 py-3 lg:py-4">
                        <span className={`px-2 lg:px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getStatusBadgeClass(tx.transactionStatusName)}`}>
                          <span className="hidden sm:inline">{tx.transactionStatusName}</span>
                          <span className="sm:hidden">
                            {tx.transactionStatusName === 'Confirmed' ? '✓' : 
                             tx.transactionStatusName === 'Pending' ? '⏱' :
                             tx.transactionStatusName === 'Failed' ? '✗' : 
                             tx.transactionStatusName.charAt(0)}
                          </span>
                        </span>
                      </td>
                      <td className="hidden xl:table-cell px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{tx.transactionDate}</div>
                      </td>
                      <td className="hidden xl:table-cell px-6 py-4">
                        <div className="text-sm text-gray-600 max-w-xs truncate" title={tx.description}>
                          {tx.description}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 mb-4">
                  <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No transactions found</h3>
                <p className="text-gray-600">
                  {searchQuery || Object.values(filters).some(v => v)
                    ? 'Try adjusting your search or filters'
                    : 'Your transaction history will appear here'}
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredTransactions.length > 0 && (
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
                {/* Page Info */}
                <div className="text-sm text-gray-700">
                  Showing <span className="font-semibold">{(currentPage - 1) * limit + 1}</span> to{' '}
                  <span className="font-semibold">
                    {Math.min(currentPage * limit, totalResults || filteredTransactions.length)}
                  </span>{' '}
                  of <span className="font-semibold">{(totalResults || filteredTransactions.length).toLocaleString('en-IN')}</span> results
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-4">
                  {/* Rows per page */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-semibold text-gray-700">Rows:</label>
                    <select
                      value={limit}
                      onChange={handleLimitChange}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 font-semibold bg-white"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  {/* Pagination Buttons */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={prevPage}
                      disabled={currentPage === 1}
                      className="px-4 py-2 bg-white border-2 border-gray-300 text-gray-700 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-all font-semibold"
                    >
                      ← Previous
                    </button>
                    
                    <div className="flex items-center gap-2 px-4">
                      <span className="text-sm font-semibold text-gray-700">
                        Page {currentPage}
                        {totalResults && ` of ${Math.ceil(totalResults / limit)}`}
                      </span>
                    </div>

                    <button
                      onClick={nextPage}
                      disabled={totalResults !== null ? currentPage * limit >= totalResults : fetchedCount < limit}
                      className="px-4 py-2 bg-white border-2 border-gray-300 text-gray-700 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-all font-semibold"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderRechargeHistory = () => {
    const filteredRechargeTransactions = rechargeSearchQuery
      ? rechargeTransactions.filter(tx =>
          tx.orderReferenceNumber.toLowerCase().includes(rechargeSearchQuery.toLowerCase()) ||
          tx.description.toLowerCase().includes(rechargeSearchQuery.toLowerCase())
        )
      : rechargeTransactions;

    return (
      <div className="space-y-6">
        {/* Action Bar - Full Width */}
        <div className="bg-white rounded-xl shadow-lg p-5 border border-gray-200">
          <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center justify-between">
            {/* Search Bar */}
            <div className="relative flex-1">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                <SearchIcon />
              </div>
              <input
                type="text"
                value={rechargeSearchQuery}
                onChange={(e) => setRechargeSearchQuery(e.target.value)}
                placeholder="Search by reference number, description..."
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
              
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowRechargeFilters(!showRechargeFilters)}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold transition-all ${
                  showRechargeFilters
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <FilterIcon />
                <span className="hidden sm:inline">Filters</span>
                <ChevronDownIcon className={`transform transition-transform ${showRechargeFilters ? 'rotate-180' : ''}`} />
              </button>

              <button
                onClick={() => {
                  fetchRechargeData();
                }}
                disabled={isLoadingRecharge}
                className="flex items-center gap-2 px-5 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold transition-all disabled:opacity-50"
                title="Refresh recharge transactions"
              >
                <RefreshIcon className={isLoadingRecharge ? 'animate-spin' : ''} />
                <span className="hidden sm:inline">Refresh</span>
              </button>

              <button
                onClick={() => alert('Export functionality - Coming soon!')}
                className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 font-semibold transition-all shadow-md"
              >
                <DownloadIcon />
                <span className="hidden sm:inline">Export</span>
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        {renderRechargeFilters()}

        {/* Recharge Transactions Table - Full Width */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            {isLoadingRecharge ? (
              <div className="p-8 space-y-4">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div key={i} className="animate-pulse flex gap-4">
                    <div className="h-12 bg-gray-200 rounded flex-1"></div>
                    <div className="h-12 bg-gray-200 rounded w-32"></div>
                    <div className="h-12 bg-gray-200 rounded w-32"></div>
                    <div className="h-12 bg-gray-200 rounded w-32"></div>
                  </div>
                ))}
              </div>
            ) : filteredRechargeTransactions.length > 0 ? (
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                  <tr>
                    <th
                      className="px-3 lg:px-6 py-3 lg:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleRechargeSort("parent_client_reference_number")}
                    >
                      <div className="flex items-center gap-1 lg:gap-2">
                        Reference Number
                        {rechargeSortBy === "parent_client_reference_number" && (
                          <span className="text-blue-600">{rechargeSortDirection === "asc" ? "↑" : "↓"}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-3 lg:px-6 py-3 lg:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleRechargeSort("transaction_amount")}
                    >
                      <div className="flex items-center gap-1 lg:gap-2">
                        Amount
                        {rechargeSortBy === "transaction_amount" && (
                          <span className="text-blue-600">{rechargeSortDirection === "asc" ? "↑" : "↓"}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-3 lg:px-6 py-3 lg:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleRechargeSort("transaction_status_id")}
                    >
                      <div className="flex items-center gap-1 lg:gap-2">
                        Status
                        {rechargeSortBy === "transaction_status_id" && (
                          <span className="text-blue-600">{rechargeSortDirection === "asc" ? "↑" : "↓"}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="hidden xl:table-cell px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleRechargeSort("transaction_date")}
                    >
                      <div className="flex items-center gap-2">
                        Date & Time
                        {rechargeSortBy === "transaction_date" && (
                          <span className="text-blue-600">{rechargeSortDirection === "asc" ? "↑" : "↓"}</span>
                        )}
                      </div>
                    </th>
                    <th className="hidden xl:table-cell px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredRechargeTransactions.map((tx, idx) => (
                    <tr
                      key={tx.walletTransactionId || idx}
                      className="hover:bg-blue-50 transition-colors duration-150"
                    >
                      <td className="px-3 lg:px-6 py-3 lg:py-4">
                        <div className="flex flex-col">
                          <div className="text-xs lg:text-sm font-medium text-gray-900 break-all">
                            {tx.orderReferenceNumber}
                          </div>
                          {/* Show date on mobile/tablet (hidden on XL+) */}
                          <div className="text-xs text-gray-500 mt-1 xl:hidden">
                            {tx.transactionDate}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 lg:px-6 py-3 lg:py-4">
                        <div className="text-xs lg:text-sm font-bold text-emerald-600 whitespace-nowrap">
                          + ₹{Number(tx.transactionAmount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </div>
                      </td>
                      <td className="px-3 lg:px-6 py-3 lg:py-4">
                        <span className={`px-2 lg:px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getStatusBadgeClass(tx.transactionStatusName)}`}>
                          <span className="hidden sm:inline">{tx.transactionStatusName}</span>
                          <span className="sm:hidden">
                            {tx.transactionStatusName === 'Confirmed' ? '✓' : 
                             tx.transactionStatusName === 'Pending' ? '⏱' :
                             tx.transactionStatusName === 'Failed' ? '✗' : 
                             tx.transactionStatusName.charAt(0)}
                          </span>
                        </span>
                      </td>
                      <td className="hidden xl:table-cell px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{tx.transactionDate}</div>
                      </td>
                      <td className="hidden xl:table-cell px-6 py-4">
                        <div className="text-sm text-gray-600 max-w-xs truncate" title={tx.description}>
                          {tx.description}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 mb-4">
                  <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No recharge transactions found</h3>
                <p className="text-gray-600">
                  {rechargeSearchQuery || Object.values(rechargeFilters).some(v => v)
                    ? 'Try adjusting your search or filters'
                    : 'Your recharge history will appear here'}
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredRechargeTransactions.length > 0 && (
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
                {/* Page Info */}
                <div className="text-sm text-gray-700">
                  Showing <span className="font-semibold">{(rechargeCurrentPage - 1) * rechargeLimit + 1}</span> to{' '}
                  <span className="font-semibold">
                    {Math.min(rechargeCurrentPage * rechargeLimit, rechargeTotalResults || filteredRechargeTransactions.length)}
                  </span>{' '}
                  of <span className="font-semibold">{(rechargeTotalResults || filteredRechargeTransactions.length).toLocaleString('en-IN')}</span> results
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-4">
                  {/* Rows per page */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-semibold text-gray-700">Rows:</label>
                    <select
                      value={rechargeLimit}
                      onChange={handleRechargeLimitChange}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 font-semibold bg-white"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  {/* Pagination Buttons */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={rechargePrevPage}
                      disabled={rechargeCurrentPage === 1}
                      className="px-4 py-2 bg-white border-2 border-gray-300 text-gray-700 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-all font-semibold"
                    >
                      ← Previous
                    </button>
                    
                    <div className="flex items-center gap-2 px-4">
                      <span className="text-sm font-semibold text-gray-700">
                        Page {rechargeCurrentPage}
                        {rechargeTotalResults && ` of ${Math.ceil(rechargeTotalResults / rechargeLimit)}`}
                      </span>
                    </div>

                    <button
                      onClick={rechargeNextPage}
                      disabled={rechargeTotalResults !== null ? rechargeCurrentPage * rechargeLimit >= rechargeTotalResults : rechargeFetchedCount < rechargeLimit}
                      className="px-4 py-2 bg-white border-2 border-gray-300 text-gray-700 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-all font-semibold"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50">
      <div className="max-w-7xl mx-auto px-2 md:px-2 lg:px-2 mt-2">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {SOURCE_MAP[source]?.title || "Wayuvega"} Wallet
            </h3>
            <p className="text-gray-600">
              Manage your wallet balance and view transaction history
            </p>
          </div>
          
          {/* Tab Navigation */}
          <div className="flex bg-white rounded-xl shadow-lg border border-gray-200 overflow-x-auto">
            {["Wallet Balance", "Order Transactions History", "Recharge History"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 lg:px-6 py-3 rounded-lg font-semibold transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab
                    ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                }`}
              >
                {tab === "Wallet Balance" ? (
                  <span className="flex items-center gap-2">
                    <WalletIcon />
                    <span className="hidden md:inline">Balance</span>
                  </span>
                ) : tab === "Order Transactions History" ? (
                  <span className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="hidden md:inline">Order History</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="hidden md:inline">Recharge</span>
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div>
        {activeTab === "Wallet Balance" && renderWalletBalance()}
        {activeTab === "Order Transactions History" && renderOrderTransactionsHistory()}
        {activeTab === "Recharge History" && renderRechargeHistory()}
      </div>

      {/* Footer Info */}
      <div className="mt-12 text-center text-gray-500 text-sm">
        <p>All transactions are secure and encrypted. Contact support for any queries.</p>
      </div>
    </div>
    </div>
  );
};

export default Wallet;

